import React, { memo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

import CardTooltip from './CardTooltip';

export interface HelpContent {
  title: string;
  /** A sentence or two under the title. */
  text?: string;
  /** One line per choice or term: [term, what it means]. */
  terms?: ReadonlyArray<readonly [string, string]>;
  footnote?: string;
}

interface Props extends HelpContent {
  /** The label the info icon sits beside; it keeps its surrounding style. */
  label: React.ReactNode;
}

/**
 * A label with a small info icon that explains it in a card tooltip — what a
 * setting means and what each of its choices does.
 */
const HelpTip = memo<Props>(({ label, title, text, terms, footnote }) => (
  <CardTooltip
    title={
      <Box sx={{ maxWidth: 300 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>{title}</Typography>
        {text && <Typography variant="body2" color="text.secondary">{text}</Typography>}
        {terms && terms.length > 0 && (
          <Stack spacing={0.75} sx={{ mt: text ? 1 : 0 }}>
            {terms.map(([term, meaning]) => (
              <Typography key={term} variant="body2">
                <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>{term}</Box> — {meaning}
              </Typography>
            ))}
          </Stack>
        )}
        {footnote && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{footnote}</Typography>
        )}
      </Box>
    }
  >
    <Box component="span" tabIndex={0} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', outline: 'none' }}>
      {label}
      <InfoOutlined aria-hidden sx={{ fontSize: '1.15em', opacity: 0.6 }} />
    </Box>
  </CardTooltip>
));

HelpTip.displayName = 'HelpTip';

export default HelpTip;
